/**
 * Découpage d'un SMS en segments facturables.
 *
 * Portage fidèle de `calculateSmsSegments` (api/src/modules/messaging/
 * notification.service.ts) : les deux doivent donner le même résultat, sinon
 * l'aperçu de l'interface promet un coût que la facture dément.
 *
 * CE QUI SE JOUE ICI
 *
 * Un SMS en alphabet GSM-7 porte 160 caractères. Un SEUL caractère hors de cet
 * alphabet — une apostrophe typographique « ’ », un accent mal placé, un emoji —
 * fait basculer le message entier en UCS-2, où le segment tombe à 70
 * caractères. Le coût double, sans avertissement. C'est exactement ce que
 * l'aperçu doit rendre visible avant l'envoi.
 */

const GSM7_RE =
	/^[@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1bÆæßÉ !"#¤%&'()*+,\-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà^{}\\[~\]|€]*$/;

/** Prix d'un segment, aligné sur `getChannelCost` côté API. */
export const SMS_SEGMENT_COST_FCFA = 15;

export interface SmsMetrics {
	segments: number;
	isUcs2: boolean;
	length: number;
	costFcfa: number;
	/** Caractères restants avant de basculer sur un segment supplémentaire. */
	remaining: number;
	/** Caractères hors GSM-7 rencontrés, dédoublonnés — pour pouvoir les montrer. */
	offenders: string[];
}

export function calculateSmsSegments(text: string): SmsMetrics {
	if (!text) {
		return {
			segments: 0,
			isUcs2: false,
			length: 0,
			costFcfa: 0,
			remaining: 160,
			offenders: [],
		};
	}

	const isUcs2 = !GSM7_RE.test(text);
	// `Array.from` et non `.length` : un emoji compte pour un caractère perçu,
	// pas pour ses deux unités de code UTF-16.
	const length = Array.from(text).length;

	const single = isUcs2 ? 70 : 160;
	const multi = isUcs2 ? 67 : 153;
	const segments = length <= single ? 1 : Math.ceil(length / multi);
	const capacity = segments === 1 ? single : segments * multi;

	return {
		segments,
		isUcs2,
		length,
		costFcfa: segments * SMS_SEGMENT_COST_FCFA,
		remaining: Math.max(capacity - length, 0),
		offenders: isUcs2 ? findOffenders(text) : [],
	};
}

/** Caractères qui font basculer le message en UCS-2. */
function findOffenders(text: string): string[] {
	const out = new Set<string>();
	for (const char of Array.from(text)) {
		if (!GSM7_RE.test(char)) out.add(char);
	}
	return [...out].slice(0, 8);
}

/**
 * Nombre de segments économisés en remplaçant l'URL longue par la courte.
 *
 * Souvent ZÉRO, et il faut l'afficher honnêtement : une URL de 107 caractères
 * dans un message de 140 tient déjà dans un seul segment. Le gain d'un lien
 * court n'est pas d'abord la facture — c'est un lien qui fonctionne, une cible
 * modifiable après envoi, et une mesure. Annoncer une économie qui n'existe pas
 * décrédibiliserait le reste.
 */
export function segmentsSaved(message: string, longUrl: string, shortUrl: string): number {
	if (!longUrl || !shortUrl) return 0;
	const withLong = calculateSmsSegments(message.replace(shortUrl, longUrl));
	const withShort = calculateSmsSegments(message);
	return Math.max(withLong.segments - withShort.segments, 0);
}
